import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetTeamByIdQuery } from 'store/api/teams';
import { EntityType } from 'types/entity';
import { TeamForm } from 'types/teams';
import { useClassifiers } from 'utils/useClassifiers';
import { TEAM_FORM_CLASSIFIERS } from './form-sections/useTeamForm';
import { teamDtoToForm } from './teamFormMapping';
import TeamPageForm from './TeamPageForm';

const ClassicTeamPageEdit = () => {
    const { id: teamId } = useParams();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: TEAM_FORM_CLASSIFIERS,
    });

    const { data: team, isLoading, isError, refetch } = useGetTeamByIdQuery(teamId ?? skipToken);

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.TEAM,
        fields: team?.customFields,
    });

    // Set initial form data
    const initialData = useMemo<TeamForm | undefined>(() => {
        if (isLoadingCustomFieldData || !classifiers || !team) {
            return undefined;
        }
        return teamDtoToForm(team, classifiers, initialCustomFieldsObject);
    }, [classifiers, team, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoading || isLoadingClassifiers || isLoadingCustomFieldData || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return (
        <TeamPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={customFields}
            team={team}
        />
    );
};

/** In the new design a team is edited card by card on its page, so its edit URL opens the Team card there. */
const TeamPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: teamId } = useParams();
    return isNew ? <Navigate to={`/team/${teamId}?edit=team`} replace /> : <ClassicTeamPageEdit />;
};

export default TeamPageEdit;
